import React, { createContext, useState, useEffect } from 'react';
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import { auth as firebaseAuth, googleProvider } from '../config/firebase';
import api from '../config/axios';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // Firebase user
  const [mongoUser, setMongoUser] = useState(null); // MongoDB user with role
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        try {
          const token = await firebaseUser.getIdToken();
          const { data } = await api.post('/auth/login', { idToken: token });
          setMongoUser(data.data.user);
        } catch (err) {
          console.error('Failed to fetch user profile:', err);
          setMongoUser(null);
          // If access denied (403), sign out of Firebase to stop retry loop
          if (err.response?.status === 403) {
            await signOut(firebaseAuth);
            setUser(null);
          }
        }
      } else {
        setMongoUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async () => {
    const result = await signInWithPopup(firebaseAuth, googleProvider);
    const token = await result.user.getIdToken();
    try {
      const { data } = await api.post('/auth/login', { idToken: token });
      setMongoUser(data.data.user);
      return data.data.user;
    } catch (err) {
      // If backend rejects (not registered / deactivated), sign out of Firebase
      await signOut(firebaseAuth);
      setUser(null);
      setMongoUser(null);
      throw err;
    }
  };

  const logout = async () => {
    await signOut(firebaseAuth);
    setUser(null);
    setMongoUser(null);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get('/auth/me');
      setMongoUser(data.data.user);
    } catch (err) {
      console.error('Failed to refresh user:', err);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        mongoUser,
        loading,
        login,
        logout,
        refreshUser,
        isAuthenticated: !!mongoUser,
        role: mongoUser?.role,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
